import axios from "axios";
import moment from "moment";

// Libs
import {getTokenFromLocalStorage} from "../auth/auth";
import utilsLib from "../common/libs/utils-lib";

// Constants
export const FETCH_TECHNICALS_SHEETS_REQUEST = "FETCH_TECHNICALS_SHEETS_REQUEST";
export const FETCH_TECHNICALS_SHEETS_SUCCESS = "FETCH_TECHNICALS_SHEETS_SUCCESS";
export const FETCH_TECHNICALS_SHEETS_ERROR = "FETCH_TECHNICALS_SHEETS_ERROR";
export const CLEAN_TECHNICALS_SHEETS_STATE = "CLEAN_TECHNICALS_SHEETS_STATE";

// Bulk download job polling
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_FAILURES = 3;
const MAX_POLL_TIME_MS = 30 * 60 * 1000;

export const readTechnicalsSheets = (filters = {}) => {
    return async (dispatch) => {
        try {
            dispatch(fetchTechnicalsSheetsRequest());

            const token = getTokenFromLocalStorage();

            const res = await axios.post('/api/technicals-sheets', {filters}, { headers: { 'Authorization': 'Bearer ' + token } });

            dispatch(utilsLib.response(res, fetchTechnicalsSheetsSuccess, fetchTechnicalsSheetsError));
        } catch (err) {
            dispatch(utilsLib.response(err, fetchTechnicalsSheetsError));
        }
    };
};

const fetchTechnicalsSheetsRequest = () => {
    return {
        type: FETCH_TECHNICALS_SHEETS_REQUEST
    };
};

const fetchTechnicalsSheetsSuccess = (payload) => {
    return {
        type: FETCH_TECHNICALS_SHEETS_SUCCESS,
        payload
    };
};

const fetchTechnicalsSheetsError = (errorMessage) => {
    return {
        type: FETCH_TECHNICALS_SHEETS_ERROR,
        errorMessage
    };
};

export const cleanTechnicalsSheetsState = () => {
    return {
        type: CLEAN_TECHNICALS_SHEETS_STATE
    }
}

/**
 * Create a bulk download job in the server, poll its progress (the server
 * downloads the technical sheets one by one, so we do not saturate SAP) and
 * finally save the generated ZIP file on the client machine.
 *
 * @param {Object} options {rows, clientId, language, onProgress}
 * @param {Array} options.rows Technical sheets (rows) to download
 * @param {string} options.clientId Selected client id
 * @param {string} options.language Selected language (S, F, D, E)
 * @param {Function} options.onProgress Callback invoked with (current, total) on each downloaded sheet
 * @returns {Promise<Object>} Summary {downloaded, failed: [{filename, message}]}
 */
export const downloadPdfsZip = async ({rows = [], clientId, language = "S", onProgress}) => {
    const headers = {'Authorization': 'Bearer ' + getTokenFromLocalStorage()};

    // Create the job (processed in background by the server)
    const createRes = await axios.post('/api/content/pdfs-zip',
        {pdfIds: rows.map(row => row._id), clientId, language},
        {headers});

    const jobId = createRes.data && createRes.data.payload && createRes.data.payload.jobId;
    if (!jobId) {
        const error = new Error("Error desconocido");
        error.response = createRes;
        throw error;
    }

    // Poll the job status until it finishes
    const startedAt = Date.now();
    let failures = 0;
    let jobStatus;

    for (;;) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

        if (Date.now() - startedAt > MAX_POLL_TIME_MS) {
            throw new Error("La descarga masiva está tardando demasiado. Inténtelo de nuevo más tarde");
        }

        let statusPayload;
        try {
            const statusRes = await axios.get(`/api/content/pdfs-zip/${jobId}`, {headers});
            statusPayload = statusRes.data && statusRes.data.payload;
            failures = 0;
        } catch (err) {
            // Tolerate a few failed polls before giving up
            failures++;
            if (failures >= MAX_POLL_FAILURES) {
                throw err;
            }
            continue;
        }

        if (!statusPayload) {
            throw new Error("Error desconocido");
        }

        if (typeof onProgress === "function") {
            onProgress(statusPayload.current, statusPayload.total, statusPayload.status);
        }

        jobStatus = statusPayload;
        if (statusPayload.status === "success" || statusPayload.status === "error") {
            break;
        }
    }

    const failed = jobStatus.failed || [];

    // The server could not download any technical sheet
    if (jobStatus.status === "error") {
        throw new Error(failed.map(failedSheet => `${failedSheet.filename}: ${failedSheet.message}`).join(" | ") || "Error desconocido");
    }

    // Download the generated ZIP file
    const zipRes = await axios.get(`/api/content/pdfs-zip/${jobId}/download`, {headers, responseType: "blob"});

    await utilsLib.saveZip(zipRes.data, `fichas-tecnicas-${moment().format("YYYYMMDD-HHmmss")}.zip`);

    // The server reports the number of sheets actually included in the ZIP (the job
    // may have been stopped by the maximum processing time before the end)
    const downloaded = typeof jobStatus.downloaded === "number"
        ? jobStatus.downloaded
        : jobStatus.total - failed.length;

    return {downloaded, failed};
};